import React, { useEffect, useState } from "react";
import { FaLink, FaDollarSign, FaExclamationTriangle, FaBoxOpen } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/Auth";
import { useGlobalContext } from "../context/stateContext";
import { IoIosTrendingUp } from "react-icons/io";
import StatCard from "../components/StatCard";
import ExpiryAlertModal from "../components/modals/ExpiryAlertModal";
import TopDrugBreakdownModal from "../components/modals/TopDrugBreakdownModal";
import { BarChart3, Eye } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { toast } from "react-toastify";
const Dashboard = () => {
  const navigate = useNavigate();
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [gettingDashbaordData, setGettingDashboardData] = useState(false);
  const [dashboardData, setDashboardData] = useState({});
  const [showExpiryAlert, setShowExpiryAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [salesTrendData, setSalesTrendData] = useState([]);
  const [branchPerformanceData, setBranchPerformanceData] = useState([]);
  const [topDrugs, setTopDrugs] = useState([]);
  const [selectedBreakdownDrug, setSelectedBreakdownDrug] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showPerformanceList, setShowPerformanceList] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const COLORS = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ef4444",
    "#ec4899",
    "#06b6d4",
    "#84cc16",
    "#f97316",
    "#6366f1",
    "#14b8a6",
    "#d946ef",
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ef4444",
    "#ec4899",
    "#06b6d4",
    "#84cc16",
    "#f97316",
    "#6366f1",
    "#14b8a6",
    "#d946ef",
  ];

  const getDashbaordData = async () => {
    setGettingDashboardData(true);
    try {
      const data = await requestWithAuth(`/users/dashboard`, "GET");
      setDashboardData(data);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setGettingDashboardData(false);
    }
  };

  const getSalesTrendData = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth(`/report/sales-trend/`, "GET");
      setSalesTrendData(Array.isArray(response) ? response : []);
    } catch (error) {
      toast.error("Failed to load sales trend data");
    } finally {
      setLoading(false);
    }
  };

  const getBranchPerformance = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth(
        `/report/branch-performance/`,
        "GET"
      );
      setBranchPerformanceData(Array.isArray(response) ? response : []);
    } catch (error) {
      toast.error("Failed to load performance data");
    } finally {
      setLoading(false);
    }
  };

  const getTopDrugs = async () => {
    try {
      const response = await requestWithAuth(`/inventory/top-moving-items/`, "GET");
      setTopDrugs(Array.isArray(response) ? response : (response?.results || []));
    } catch (error) {
      console.error("Failed to load top moving drugs:", error);
    }
  };

  // Check if alert should be shown
  useEffect(() => {
    const checkExpiryAlert = () => {
      const today = new Date().toISOString().split("T")[0];
      const dismissedDate = localStorage.getItem("expiryAlertDismissed");
      const hasExpiryIssues =
        dashboardData?.expiring_drugs > 0 || dashboardData?.expired_drugs > 0;
      const shouldShow = hasExpiryIssues && dismissedDate !== today;

      if (shouldShow) {
        setTimeout(() => {
          setShowExpiryAlert(true);
        }, 500);
      }
    };

    if (!gettingDashbaordData) {
      checkExpiryAlert();
    }
  }, [
    gettingDashbaordData,
    dashboardData?.expiring_drugs,
    dashboardData?.expired_drugs,
  ]);

  const handleCloseExpiryAlert = () => {
    setShowExpiryAlert(false);
  };

  const handleCloseAndHideExpiryAlert = () => {
    if (dashboardData?.expired_drugs > 0) {
      toast.error(
        `You have ${dashboardData?.expired_drugs} expired drugs, please clear them before continuing`
      );
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    // localStorage.setItem("expiryAlertDismissed", today);
    setShowExpiryAlert(false);
  };

  const handleViewExpiringDrugs = () => {
    const branch_id = activeSession?.branch?.id;
    if (dashboardData?.expired_drugs === 0) {
      const today = new Date().toISOString().split("T")[0];
      // localStorage.setItem("expiryAlertDismissed", today);
    }
    setShowExpiryAlert(false);
    navigate(`/inventory/branch/expiring-drugs/${branch_id}`);
  };

  useEffect(() => {
    getDashbaordData();
    getSalesTrendData();
    getBranchPerformance();
    getTopDrugs();
  }, []);


  const SimpleCard = ({ title, value, linkTo, color = "bg-gray-100", subtitle }) => (
    <div className={`rounded-lg p-6 ${color} text-center`}>
      <h3 className="text-3xl font-bold mb-2">{value}</h3>
      <p className="text-sm opacity-90 mb-3">{title}</p>
      {subtitle && (
        <p className="text-xs opacity-75 mb-3">{subtitle}</p>
      )}
      {linkTo && (
        <Link to={linkTo} className="inline-flex items-center gap-1 text-sm hover:underline">
          View Details <FaLink className="text-[12px]" />
        </Link>
      )}
    </div>
  );

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatCount = (count) => {
    return Number(count || 0).toLocaleString('en-US');
  };

  if (gettingDashbaordData) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome - {activeSession?.user?.first_name}
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-200 rounded-lg h-32 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen">
      <ExpiryAlertModal
        isOpen={showExpiryAlert}
        onClose={handleCloseExpiryAlert}
        onHideModal={handleCloseAndHideExpiryAlert}
        expiringDrugs={dashboardData.expiring_drugs}
        expiredDrugs={dashboardData.expired_drugs}
        onViewDetails={handleViewExpiringDrugs}
      />
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome {activeSession?.user?.first_name}
          </h1>
          <p className="text-gray-600">Here's what's happening with your business today</p>
        </div>

        <div className="bg-white px-4 py-2 rounded-xl border border-gray-200 shadow-sm flex flex-col items-end">
          <span className="text-sm font-semibold text-gray-900">
            {currentTime.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
          <span className="text-2xl font-mono font-bold text-blue-600 tracking-wider">
            {currentTime.toLocaleTimeString('en-US', {
              hour12: true,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </span>
        </div>
      </div>

      {/* SMS Bundle Alert */}
      {dashboardData?.sms_bundle_left !== undefined && dashboardData?.sms_bundle_left < 20 && (
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <FaExclamationTriangle className="text-red-500" size={20} />
            <div>
              <p className="text-sm text-red-800">
                <strong>SMS Bundle Low:</strong> You have only <strong>{dashboardData.sms_bundle_left}</strong> SMS units left.
              </p>
            </div>
          </div>
          <Link
            to="/administration/sms-bundle"
            className="text-sm font-semibold text-red-600 hover:text-red-700 bg-white px-4 py-2 rounded-lg border border-red-200 transition-colors shadow-sm"
          >
            Top up now
          </Link>
        </div>
      )}

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <StatCard
          title="Today's Sales"
          value={`GHS ${formatAmount(dashboardData?.sales)}`}

          additionalInfo={`Customers Served: GHs ${formatAmount(dashboardData?.customers_served)} (${formatCount(dashboardData?.number_customers_served)})`}
          icon={FaDollarSign}
          color="bg-blue-500"
          linkTo="/sales/today"
          onRefresh={getDashbaordData}
        />

        <StatCard
          title="Outstanding Invoices"
          value={`GHS ${formatAmount(dashboardData?.outstanding_invoices)}`}
          subtitle="Outstanding Supplier Invoices"
          additionalInfo={`Total Suppliers: ${formatCount(dashboardData?.total_suppliers)}`}
          icon={FaExclamationTriangle}
          color="bg-red-500"
          linkTo="/transaction/supplier"
          onRefresh={getDashbaordData}
        />

        <StatCard
          title="Low Stock Alert"
          value={formatCount(dashboardData?.items_below_reorder)}
          subtitle="Items Below reorder level"
          icon={FaBoxOpen}
          color="bg-yellow-500"
          linkTo="/inventory/reorder"
          onRefresh={getDashbaordData}
        />

        <StatCard
          title="Stock Value"
          value={`GHS ${formatAmount(dashboardData?.total_stock_outstanding)}`}
          subtitle="Total Stock Outstanding"
          additionalInfo={`Drug Stock: ${formatAmount(dashboardData?.drug_stock)}`}
          icon={IoIosTrendingUp}
          color="bg-green-500"
          linkTo="/stock/outstanding"
          onRefresh={getDashbaordData}
        />

        <StatCard
          title="Expiry Alert"
          value={formatCount(dashboardData?.expiring_drugs)}
          subtitle="Items that are about to expire"
          additionalInfo={dashboardData?.expired_drugs > 0 ? `${dashboardData?.expired_drugs} items expired` : "No items expired"}
          icon={FaExclamationTriangle}
          color="bg-red-600"
          linkTo={`/inventory/branch/expiring-drugs/${activeSession?.branch?.id}`}
          onRefresh={getDashbaordData}
        />
      </div>

      {/* Secondary Stats */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Branch/Financial Management</h2>
        <p className="text-sm text-gray-600 mb-4">Transaction Summary</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SimpleCard
          title="Pending Item Returns"
          value={formatCount(dashboardData?.pending_returns)}
          subtitle="Items pending return"
          linkTo="/pos/return"
          color="bg-white border border-gray-200 text-gray-800"
        />

        <SimpleCard
          title="Invoice Report"
          value={formatCount(dashboardData?.invoice_report)}
          linkTo="/report/purchases/invoice"
          color="bg-teal-500 text-white"
        />

        <SimpleCard
          title="Pending Requisition"
          value={formatCount(dashboardData?.pending_requisitions)}
          subtitle=""
          linkTo="/authorize/requisition"
          color="bg-purple-500 text-white"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 my-8">
        {/* Sales Trend Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 size={20} className="text-blue-600" />
                Sales Trend Analysis
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Monthly performance vs last year
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={salesTrendData || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="sales"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Current Year"
              />
              <Line
                type="monotone"
                dataKey="last_year"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Last Year"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Branch Performance Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Branch Performance
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Sales distribution by branch this year
              </p>
            </div>
            <button
              onClick={() => setShowPerformanceList(!showPerformanceList)}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
            >
              {showPerformanceList ? "Show Chart" : "View Data List"}
            </button>
          </div>

          {showPerformanceList ? (
            <div className="h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Branch Name
                    </th>
                    <th scope="col" className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Sales (GHS)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {(branchPerformanceData || []).map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full inline-block"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        {entry.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                        GHS {Number(entry.value).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={branchPerformanceData || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  nameKey="name"
                >
                  {branchPerformanceData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                  formatter={(value, name) => [`GHS ${Number(value).toFixed(2)}`, name]}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => <span className="text-gray-600 font-medium ml-1">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top Moving Drugs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Top 50 Moving Drugs</h2>
            <p className="text-sm text-gray-600 mt-1">Most frequently sold items</p>
          </div>
        </div>
        <div className="overflow-y-auto h-96 pr-4 custom-scrollbar">
          {topDrugs?.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {topDrugs.map((drug, idx) => (
                <li key={idx} className="py-3 flex justify-between items-center hover:bg-gray-50 px-2 rounded-lg transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 text-blue-700 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm">
                      {idx + 1}
                    </div>
                    <p className="font-medium text-gray-900">
                      {drug?.name || drug?.drug_name || drug?.item_name || 'Unknown Item'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <p className="text-sm font-semibold text-gray-700 w-24">
                      {drug?.quantity || drug?.quantity_sold || drug?.total_quantity || drug?.total_sold || 0} times sold
                    </p>
                    <button
                      onClick={() => setSelectedBreakdownDrug(drug)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Eye size={14} />
                      Breakdown
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 text-center py-10">No top moving drugs data available</p>
          )}
        </div>
      </div>

      <TopDrugBreakdownModal
        isOpen={!!selectedBreakdownDrug}
        onClose={() => setSelectedBreakdownDrug(null)}
        drug={selectedBreakdownDrug}
      />
    </div>
  );
};

export default Dashboard;